import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

const getDefaultRecentlyDeletedMeta = () => ({
    count: 0,
    next: null,
    previous: null,
});

const parseRecentlyDeletedResponse = (payload) => {
    const safePayload = payload || {};
    const items = Array.isArray(safePayload.results?.recently_deleted)
        ? safePayload.results.recently_deleted
        : Array.isArray(safePayload.results)
            ? safePayload.results
            : Array.isArray(safePayload.recently_deleted)
                ? safePayload.recently_deleted
                : [];

    return {
        items,
        meta: {
            count: typeof safePayload.count === "number" ? safePayload.count : items.length,
            next: safePayload.next ?? null,
            previous: safePayload.previous ?? null,
        },
    };
};

export const changePassword = createAsyncThunk("changePassword", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/change-password/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchSessions = createAsyncThunk("fetchSessions", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/oauth/sessions/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const deleteAccount = createAsyncThunk("deleteAccount", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/delete-account/initiate/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchRecentlyDeletedPosts = createAsyncThunk("fetchRecentlyDeletedPosts", async (nextUrl, { rejectWithValue }) => {
    try {

        let response;
        if (nextUrl) {
            const url = new URL(nextUrl);
            const pathAndQuery = url.pathname + url.search;
            const relativePath = pathAndQuery.replace('/api', '');
            response = await axiosInstance.get(relativePath);
        } else {
            response = await axiosInstance.get(`/post/recently-deleted/`);
        }
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const restoreRecentlyDeletedPost = createAsyncThunk("restoreRecentlyDeletedPost", async (postId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/post/recently-deleted/${postId}/restore/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const permanentlyDeleteRecentlyDeletedPost = createAsyncThunk(
    "permanentlyDeleteRecentlyDeletedPost",
    async (postId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/post/recently-deleted/${postId}/permanent-delete/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const emptyRecentlyDeletedPosts = createAsyncThunk("emptyRecentlyDeletedPosts", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/post/recently-deleted/empty/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const screenTime = createAsyncThunk("screenTime", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/activity/week/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateFullName = createAsyncThunk("updateFullName", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/edit_names/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateDateOfBirth = createAsyncThunk("updateDateOfBirth", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/date-of-birth/update/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const getDateOfBirth = createAsyncThunk("getDateOfBirth", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/date-of-birth/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const accountHistory = createAsyncThunk(
    "accountHistory",
    async ({ page = 1, url, append = false, action = "" } = {}, { rejectWithValue }) => {
        try {
            const endpoint = url || `/your-activity/history/`;
            const config = url ? {} : { params: { page, ...(action ? { action } : {}) } };
            const response = await axiosInstance.get(endpoint, config);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteHistoryItem = createAsyncThunk(
    "deleteHistoryItem",
    async (historyId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/your-activity/history/${historyId}/delete/`);
            return { ...response.data, historyId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const clearAllHistory = createAsyncThunk(
    "clearAllHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/your-activity/history/clear/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const changeEmail = createAsyncThunk("changeEmail", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/change-email/request/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const verifyEmailOtp = createAsyncThunk("verifyEmailOtp", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/change-email/verify/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const logoutSpecificSession = createAsyncThunk("logoutSpecificSession", async (sessionId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/logout-device/`, { session_key: sessionId });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const changePhone = createAsyncThunk("changePhone", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/change-phone/request/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const verifyPhoneOtp = createAsyncThunk("verifyPhoneOtp", async (data, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/oauth/change-phone/verify/`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});


export const AccountSetting = createSlice({
    name: "AccountSetting",
    reducers: {
        clearFullNameError: (state) => {
            state.fullNameError = null;
        },
        clearDeleteAccountError: (state) => {
            state.deleteAccountError = null;
            state.deleteAccountSuccess = false;
        },
    },
    initialState: {
        loading: false,
        error: null,
        sessions: [],
        sessionsLoading: false,
        sessionsError: null,
        deleteAccountLoading: false,
        deleteAccountError: null,
        deleteAccountSuccess: false,
        commentData: null,
        recentlyDeleted: [],
        recentlyDeletedMeta: getDefaultRecentlyDeletedMeta(),
        recentlyDeletedLoading: false,
        recentlyDeletedError: null,
        restorePostLoading: false,
        restorePostError: null,
        permanentDeletePostLoading: false,
        permanentDeletePostError: null,
        emptyRecentlyDeletedLoading: false,
        emptyRecentlyDeletedError: null,
        screenTimeData: null,
        screenTimeLoading: false,
        screenTimeError: null,
        fullNameData: null,
        fullNameLoading: false,
        fullNameError: null,
        dateOfBirthData: null,
        dateOfBirthLoading: false,
        dateOfBirthError: null,
        accountHistoryData: null,
        accountHistoryLoading: false,
        accountHistoryError: null,
        accountHistoryItems: [],
        accountHistoryMeta: {
            count: 0,
            next: null,
            previous: null,
            totalCount: 0,
        },
        accountHistoryLoadingMore: false,
        deleteHistoryLoading: false,
        deleteHistoryError: null,
        clearHistoryLoading: false,
        clearHistoryError: null,
        changePhoneLoading: false,
        changePhoneError: null,
        verifyPhoneLoading: false,
        verifyPhoneError: null,
    },
    extraReducers: (builder) => {
        // change password
        builder.addCase(changePassword.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(changePassword.fulfilled, (state, action) => {
            state.loading = false;
            state.commentData = action.payload;
        });
        builder.addCase(changePassword.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // fetch sessions
        builder.addCase(fetchSessions.pending, (state) => {
            state.sessionsLoading = true;
            state.sessionsError = null;
        });
        builder.addCase(fetchSessions.fulfilled, (state, action) => {
            state.sessionsLoading = false;
            state.sessions = action.payload.sessions || [];
        });
        builder.addCase(fetchSessions.rejected, (state, action) => {
            state.sessionsLoading = false;
            state.sessionsError = action.payload;
        });

        // delete account
        builder.addCase(deleteAccount.pending, (state) => {
            state.deleteAccountLoading = true;
            state.deleteAccountError = null;
            state.deleteAccountSuccess = false;
        });
        builder.addCase(deleteAccount.fulfilled, (state, action) => {
            state.deleteAccountLoading = false;
            state.deleteAccountSuccess = true;
        });
        builder.addCase(deleteAccount.rejected, (state, action) => {
            state.deleteAccountLoading = false;
            state.deleteAccountError = action.payload;
            state.deleteAccountSuccess = false;
        });

        // recently deleted posts
        builder.addCase(fetchRecentlyDeletedPosts.pending, (state) => {
            state.recentlyDeletedLoading = true;
            state.recentlyDeletedError = null;
        });
        builder.addCase(fetchRecentlyDeletedPosts.fulfilled, (state, action) => {
            state.recentlyDeletedLoading = false;
            const { items, meta } = parseRecentlyDeletedResponse(action.payload);
            // If nextUrl was provided, we're paginating - append items
            // Otherwise, it's initial load - replace items
            const isPaginating = !!action.meta.arg;
            state.recentlyDeleted = isPaginating
                ? [...state.recentlyDeleted, ...items]
                : items;
            state.recentlyDeletedMeta = meta;
        });
        builder.addCase(fetchRecentlyDeletedPosts.rejected, (state, action) => {
            state.recentlyDeletedLoading = false;
            state.recentlyDeletedError = action.payload || action.error?.message || "Failed to load recently deleted posts.";
        });

        // restore post
        builder.addCase(restoreRecentlyDeletedPost.pending, (state) => {
            state.restorePostLoading = true;
            state.restorePostError = null;
        });
        builder.addCase(restoreRecentlyDeletedPost.fulfilled, (state, action) => {
            state.restorePostLoading = false;
            const restoredId = action.payload?.post?.id || action.meta.arg;
            if (restoredId) {
                state.recentlyDeleted = state.recentlyDeleted.filter((item) => item.id !== restoredId);
                if (state.recentlyDeletedMeta.count > 0) {
                    state.recentlyDeletedMeta.count = Math.max(state.recentlyDeletedMeta.count - 1, 0);
                }
            }
        });
        builder.addCase(restoreRecentlyDeletedPost.rejected, (state, action) => {
            state.restorePostLoading = false;
            state.restorePostError = action.payload || action.error?.message || "Failed to restore post.";
        });

        // permanently delete single post
        builder.addCase(permanentlyDeleteRecentlyDeletedPost.pending, (state) => {
            state.permanentDeletePostLoading = true;
            state.permanentDeletePostError = null;
        });
        builder.addCase(permanentlyDeleteRecentlyDeletedPost.fulfilled, (state, action) => {
            state.permanentDeletePostLoading = false;
            const deletedId = action.meta.arg;
            if (deletedId) {
                state.recentlyDeleted = state.recentlyDeleted.filter((item) => item.id !== deletedId);
                if (state.recentlyDeletedMeta.count > 0) {
                    state.recentlyDeletedMeta.count = Math.max(state.recentlyDeletedMeta.count - 1, 0);
                }
            }
        });
        builder.addCase(permanentlyDeleteRecentlyDeletedPost.rejected, (state, action) => {
            state.permanentDeletePostLoading = false;
            state.permanentDeletePostError = action.payload || action.error?.message || "Failed to permanently delete post.";
        });

        // empty recently deleted
        builder.addCase(emptyRecentlyDeletedPosts.pending, (state) => {
            state.emptyRecentlyDeletedLoading = true;
            state.emptyRecentlyDeletedError = null;
        });
        builder.addCase(emptyRecentlyDeletedPosts.fulfilled, (state) => {
            state.emptyRecentlyDeletedLoading = false;
            state.recentlyDeleted = [];
            state.recentlyDeletedMeta = getDefaultRecentlyDeletedMeta();
        });
        builder.addCase(emptyRecentlyDeletedPosts.rejected, (state, action) => {
            state.emptyRecentlyDeletedLoading = false;
            state.emptyRecentlyDeletedError = action.payload || action.error?.message || "Failed to empty Recently Deleted.";
        });

        // screen time
        builder.addCase(screenTime.pending, (state) => {
            state.screenTimeLoading = true;
            state.screenTimeError = null;
        });
        builder.addCase(screenTime.fulfilled, (state, action) => {
            state.screenTimeLoading = false;
            state.screenTimeData = action.payload;
        });
        builder.addCase(screenTime.rejected, (state, action) => {
            state.screenTimeLoading = false;
            state.screenTimeError = action.payload;
        });

        // update full name
        builder.addCase(updateFullName.pending, (state) => {
            state.fullNameLoading = true;
            state.fullNameError = null;
        });
        builder.addCase(updateFullName.fulfilled, (state, action) => {
            state.fullNameLoading = false;
            state.fullNameData = action.payload;
        });
        builder.addCase(updateFullName.rejected, (state, action) => {
            state.fullNameLoading = false;
            state.fullNameError = action.payload;
        });

        // update date of birth
        builder.addCase(updateDateOfBirth.pending, (state) => {
            state.dateOfBirthLoading = true;
            state.dateOfBirthError = null;
        });
        builder.addCase(updateDateOfBirth.fulfilled, (state, action) => {
            state.dateOfBirthLoading = false;
            state.dateOfBirthData = action.payload;
        });
        builder.addCase(updateDateOfBirth.rejected, (state, action) => {
            state.dateOfBirthLoading = false;
            state.dateOfBirthError = action.payload;
        });

        // get date of birth
        builder.addCase(getDateOfBirth.pending, (state) => {
            state.dateOfBirthLoading = true;
            state.dateOfBirthError = null;
        });
        builder.addCase(getDateOfBirth.fulfilled, (state, action) => {
            state.dateOfBirthLoading = false;
            state.dateOfBirthData = action.payload;
        });
        builder.addCase(getDateOfBirth.rejected, (state, action) => {
            state.dateOfBirthLoading = false;
            state.dateOfBirthError = action.payload;
        });

        // account history
        builder.addCase(accountHistory.pending, (state, action) => {
            const isAppend = action.meta?.arg?.append;
            if (isAppend) {
                state.accountHistoryLoadingMore = true;
            } else {
                state.accountHistoryLoading = true;
            }
            state.accountHistoryError = null;
        });
        builder.addCase(accountHistory.fulfilled, (state, action) => {
            const history = action.payload?.results?.history || [];
            const meta = {
                count: typeof action.payload?.count === "number" ? action.payload.count : history.length,
                next: action.payload?.next ?? null,
                previous: action.payload?.previous ?? null,
                totalCount:
                    typeof action.payload?.results?.total_count === "number"
                        ? action.payload.results.total_count
                        : typeof action.payload?.count === "number"
                            ? action.payload.count
                            : history.length,
            };

            if (action.meta?.arg?.append) {
                state.accountHistoryItems = [...state.accountHistoryItems, ...history];
            } else {
                state.accountHistoryItems = history;
            }

            state.accountHistoryMeta = meta;
            state.accountHistoryData = action.payload;
            state.accountHistoryLoading = false;
            state.accountHistoryLoadingMore = false;
        });
        builder.addCase(accountHistory.rejected, (state, action) => {
            state.accountHistoryLoading = false;
            state.accountHistoryLoadingMore = false;
            state.accountHistoryError =
                action.payload || action.error?.message || "Failed to load account history.";
        });

        // delete single history item
        builder.addCase(deleteHistoryItem.pending, (state) => {
            state.deleteHistoryLoading = true;
            state.deleteHistoryError = null;
        });
        builder.addCase(deleteHistoryItem.fulfilled, (state, action) => {
            state.deleteHistoryLoading = false;
            state.accountHistoryItems = state.accountHistoryItems.filter(
                (item) => item.id !== action.payload.historyId
            );
            if (state.accountHistoryMeta.count > 0) {
                state.accountHistoryMeta.count -= 1;
            }
        });
        builder.addCase(deleteHistoryItem.rejected, (state, action) => {
            state.deleteHistoryLoading = false;
            state.deleteHistoryError = action.payload || action.error?.message || "Failed to delete history item.";
        });

        // clear all history
        builder.addCase(clearAllHistory.pending, (state) => {
            state.clearHistoryLoading = true;
            state.clearHistoryError = null;
        });
        builder.addCase(clearAllHistory.fulfilled, (state) => {
            state.clearHistoryLoading = false;
            state.accountHistoryItems = [];
            state.accountHistoryMeta = { count: 0, next: null, previous: null, totalCount: 0 };
        });
        builder.addCase(clearAllHistory.rejected, (state, action) => {
            state.clearHistoryLoading = false;
            state.clearHistoryError = action.payload || action.error?.message || "Failed to clear account history.";
        });

        // change email
        builder.addCase(changeEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(changeEmail.fulfilled, (state, action) => {
            state.loading = false;
            state.commentData = action.payload;
        });
        builder.addCase(changeEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // verify email otp
        builder.addCase(verifyEmailOtp.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(verifyEmailOtp.fulfilled, (state, action) => {
            state.loading = false;
            state.commentData = action.payload;
        });
        builder.addCase(verifyEmailOtp.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change phone
        builder.addCase(changePhone.pending, (state) => {
            state.changePhoneLoading = true;
            state.changePhoneError = null;
        });
        builder.addCase(changePhone.fulfilled, (state) => {
            state.changePhoneLoading = false;
        });
        builder.addCase(changePhone.rejected, (state, action) => {
            state.changePhoneLoading = false;
            state.changePhoneError = action.payload;
        });

        // verify phone otp
        builder.addCase(verifyPhoneOtp.pending, (state) => {
            state.verifyPhoneLoading = true;
            state.verifyPhoneError = null;
        });
        builder.addCase(verifyPhoneOtp.fulfilled, (state) => {
            state.verifyPhoneLoading = false;
        });
        builder.addCase(verifyPhoneOtp.rejected, (state, action) => {
            state.verifyPhoneLoading = false;
            state.verifyPhoneError = action.payload;
        });

        // logout specific session
        builder.addCase(logoutSpecificSession.pending, (state) => {
            // state.sessionsLoading = true;
            state.sessionsError = null;
        });
        builder.addCase(logoutSpecificSession.fulfilled, (state, action) => {
            state.sessionsLoading = false;
            const sessionId = action.meta.arg;
            state.sessions = state.sessions.filter((session) => session.session_key !== sessionId);
        });
        builder.addCase(logoutSpecificSession.rejected, (state, action) => {
            state.sessionsLoading = false;
            state.sessionsError = action.payload || action.error?.message || "Failed to logout from session.";
        });
    },
});

export const { clearFullNameError, clearDeleteAccountError } = AccountSetting.actions;
export default AccountSetting.reducer;